import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUpload, FiDownload, FiSettings, FiZap, FiInfo, FiCheck, FiX } = FiIcons;

function ImageCompressor() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [compressedImage, setCompressedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [compressionLevel, setCompressionLevel] = useState(80);
  const [outputFormat, setOutputFormat] = useState('jpeg');
  const [processingTime, setProcessingTime] = useState(0);

  const handleImageSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      setOriginalSize(file.size);
      setCompressedImage(null);
      setCompressedSize(0);
      setProcessingTime(0);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      setOriginalSize(file.size);
      setCompressedImage(null);
      setCompressedSize(0);
      setProcessingTime(0);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const compressImage = async () => {
    if (!selectedImage) return;

    setIsProcessing(true);
    const startTime = Date.now();

    try {
      // Create canvas for image compression
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Set canvas dimensions to original image dimensions
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image on canvas
        ctx.drawImage(img, 0, 0, img.width, img.height);

        // Convert to blob with compression
        const quality = compressionLevel / 100;
        const mimeType = outputFormat === 'png' ? 'image/png' : 
                        outputFormat === 'webp' ? 'image/webp' : 'image/jpeg';

        canvas.toBlob((blob) => {
          setCompressedImage(blob);
          setCompressedSize(blob.size);
          setProcessingTime(Date.now() - startTime);
          setIsProcessing(false);

          // Track compression analytics
          if (typeof gtag !== 'undefined') {
            gtag('event', 'image_compressed', {
              event_category: 'Image Tools',
              event_label: 'Single Image Compression',
              value: Math.round(((originalSize - blob.size) / originalSize) * 100)
            });
          }
        }, mimeType, quality);
      };

      img.src = URL.createObjectURL(selectedImage);
    } catch (error) {
      console.error('Compression failed:', error);
      setIsProcessing(false);
    }
  };

  const downloadCompressed = () => {
    if (!compressedImage) return;

    const url = URL.createObjectURL(compressedImage);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compressed_${selectedImage.name.split('.')[0]}.${outputFormat}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Track download analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'compressed_image_downloaded', {
        event_category: 'Image Tools',
        event_label: 'Single Image Download'
      });
    }
  };

  const resetTool = () => {
    setSelectedImage(null);
    setCompressedImage(null);
    setOriginalSize(0);
    setCompressedSize(0);
    setProcessingTime(0);
    setCompressionLevel(80);
    setOutputFormat('jpeg');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const compressionRatio = originalSize > 0 ? Math.round(((originalSize - compressedSize) / originalSize) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiZap} className="text-green-600 text-2xl" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Image Compressor</h2>
          <p className="text-gray-600">Reduce image file size while maintaining quality</p>
        </div>

        {!selectedImage ? (
          // Upload Area
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-green-500 transition-colors cursor-pointer"
          >
            <input
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
              id="image-upload"
            />
            <label htmlFor="image-upload" className="cursor-pointer">
              <SafeIcon icon={FiUpload} className="text-4xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Choose Image to Compress
              </h3>
              <p className="text-gray-500 mb-4">
                Drag and drop an image here, or click to select
              </p>
              <div className="text-sm text-gray-400">
                Supports: JPEG, PNG, WebP, GIF (Max: 50MB)
              </div>
            </label>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Image Preview and Settings */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Original Image */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Original Image</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <img
                    src={URL.createObjectURL(selectedImage)}
                    alt="Original"
                    className="w-full h-48 object-contain rounded-lg mb-4"
                  />
                  <div className="text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Size:</span>
                      <span className="font-medium">{formatFileSize(originalSize)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Format:</span>
                      <span className="font-medium">{selectedImage.type.split('/')[1].toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compression Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compression Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quality Level: {compressionLevel}%
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={compressionLevel}
                      onChange={(e) => setCompressionLevel(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Higher Compression</span>
                      <span>Better Quality</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Output Format
                    </label>
                    <select
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="jpeg">JPEG (Best compression)</option>
                      <option value="png">PNG (Lossless)</option>
                      <option value="webp">WebP (Modern format)</option>
                    </select>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <SafeIcon icon={FiInfo} className="text-blue-600 mt-0.5 mr-2" />
                      <div className="text-sm text-blue-800">
                        <strong>Quality Guide:</strong>
                        <ul className="mt-1 space-y-1">
                          <li>• 90-100%: Highest quality, larger file</li>
                          <li>• 70-89%: Good balance (recommended)</li>
                          <li>• 50-69%: Noticeable compression</li>
                          <li>• 10-49%: High compression, quality loss</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={compressImage}
                disabled={isProcessing}
                className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Compressing...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiZap} className="mr-2" />
                    Compress Image
                  </>
                )}
              </motion.button>

              <button
                onClick={resetTool}
                className="bg-gray-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <SafeIcon icon={FiX} className="mr-2" />
                Reset
              </button>
            </div>

            {/* Compressed Result */}
            {compressedImage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border border-green-200 rounded-xl p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-green-800 flex items-center">
                    <SafeIcon icon={FiCheck} className="mr-2" />
                    Compression Complete!
                  </h3>
                  <span className="text-sm text-green-600">
                    Processed in {processingTime}ms
                  </span>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Compressed Preview</h4>
                    <img
                      src={URL.createObjectURL(compressedImage)}
                      alt="Compressed"
                      className="w-full h-48 object-contain rounded-lg bg-white border"
                    />
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Compression Stats</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Original Size:</span>
                        <span className="font-medium">{formatFileSize(originalSize)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Compressed Size:</span>
                        <span className="font-medium">{formatFileSize(compressedSize)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Space Saved:</span>
                        <span className="font-medium text-green-600">
                          {formatFileSize(originalSize - compressedSize)} ({compressionRatio}%)
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Quality Level:</span>
                        <span className="font-medium">{compressionLevel}%</span>
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={downloadCompressed}
                      className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center mt-6"
                    >
                      <SafeIcon icon={FiDownload} className="mr-2" />
                      Download Compressed Image
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default ImageCompressor;